import {AppConfig, BaseAppProps, BaseAppState} from "shared/BaseApp";
import React, {createRef, ReactElement, RefObject} from 'react';
import {createTheme, ThemeOptions} from "@mui/material";
import {black, DEFAULT_THEME_OPTIONS} from "../shared/colors";
import {Main} from "./Main";
import {PathComponent} from "../index";
import {FIREBASE_CONFIG} from "../consts";
import {BORDER_RADIUS, SZ_MD} from "../shared/dimens";
import {Auth, indexedDBLocalPersistence, initializeAuth, User} from "@firebase/auth";
import {FirebaseApp} from "firebase/app";
import {Capacitor} from "@capacitor/core";
import {ProvisionedApp} from "../shared/ProvisionedApp";
import {AppPrefListener, ProvisionedAppConfig} from "../shared/types";
import {AppPrefs} from "./app_prefs";
import {BaseAppPrefs} from "../shared/BaseAppPrefs";
import {MessagesContextTrait} from "./shared/messages/types";
import {ParticipantKey, ParticipantType} from "./shared/messages/chat/types";
import {getMemberAuth} from "../shared/auth";
import {Member, Members, User as UserEntity} from "../shared/entities";
import {CHAT_PERSONAS} from "../toktiv-common/types";
import {Avatar} from "./avatarmaker/avatar";
import {CHAT_PERSONA_APPEARANCE_KTM} from "./types";

export type AppProps = BaseAppProps & {}

type AppState = BaseAppState & {}

export const COLOR_PRIMARY = black;

export const THEME_BG = "linear-gradient( 15deg,  rgba(218,255,234,1) 5.3%, rgba(232,255,231,1) 11.6%, rgba(240,255,233,1) 17.4%, rgba(243,255,226,1) 22.9%, rgba(253,255,228,1) 28.5%, rgba(255,248,225,1) 33.9%, rgba(255,241,229,1) 39.4%, rgba(255,228,222,1) 44.8%, rgba(255,231,231,1) 50.3%, rgba(255,231,243,1) 56.9%, rgba(255,229,255,1) 63.2%, rgba(245,228,255,1) 69.1%, rgba(236,228,255,1) 74.7%, rgba(231,238,255,1) 80.4%, rgba(229,249,255,1) 85.8%, rgba(231,255,250,1) 90.6%, rgba(229,255,243,1) 94.4%, rgba(220,255,228,1) 98.3% )";

export const THEME_OPTIONS: ThemeOptions = {
  ...DEFAULT_THEME_OPTIONS,
  components: {
    ...DEFAULT_THEME_OPTIONS.components,
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: "bold",
          fontSize: "75%",
          borderRadius: BORDER_RADIUS,
        },
      }
    },
  },
  typography: {
    h1: {
      fontWeight: "bold",
      lineHeight: 1,
    },
    h2: {
      fontWeight: "bold",
      lineHeight: 1,
    },
    h3: {
      fontWeight: "bold",
      lineHeight: 1,
    },
    h4: {
      fontWeight: "bold",
      lineHeight: 1,
    },
    h5: {
      fontWeight: "bold",
      lineHeight: 1,
    },
    h6: {
      fontWeight: "bold",
      lineHeight: 1,
    },
    fontFamily: "Poppins, sans-serif",
    fontSize: 15,
    // fontFamily: 'THICCCBOI, sans-serif',
  },
  palette: {
    primary: {
      main: COLOR_PRIMARY,
      contrastText: "#fff",
    },
    secondary: {
      main: "#000",
      contrastText: "#fff",
    },
  }
};

export const THEME = createTheme(THEME_OPTIONS);

export class AppRefs {

  constructor(
    readonly audio: HTMLAudioElement,
    readonly localAudioRenderer: RefObject<HTMLAudioElement>,
    readonly localAudioElement: ReactElement,
    readonly localVideoRenderer: RefObject<HTMLVideoElement>,
    readonly localVideoElement: ReactElement,
    readonly remoteAudioRenderer: RefObject<HTMLAudioElement>,
    readonly remoteAudioElement: ReactElement,
    readonly remoteVideoRenderer: RefObject<HTMLVideoElement>,
    readonly remoteVideoElement: ReactElement) {
  }
}

export class App extends ProvisionedApp<AppProps, AppState>
  implements AppPrefListener,
    MessagesContextTrait {

  private static appRefs: AppRefs;

  static getAppRefs(): AppRefs {
    if (!App.appRefs) {
      App.appRefs = App.createAppRefs();
    }
    return App.appRefs;
  }

  private static createAudioElement(ref: RefObject<HTMLAudioElement>, muted: boolean): ReactElement {
    return <audio ref={ref} muted={muted}/>;
  }

  private static createVideoElement(ref: RefObject<HTMLVideoElement>): ReactElement {
    return <video ref={ref} autoPlay playsInline muted
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: BORDER_RADIUS,
                  }}></video>;
  }

  private static createAppRefs() {
    const localAudioRenderer = createRef<HTMLAudioElement>();
    const localVideoRenderer = createRef<HTMLVideoElement>();
    const remoteAudioRenderer = createRef<HTMLAudioElement>();
    const remoteVideoRenderer = createRef<HTMLVideoElement>();
    return new AppRefs(
      document.getElementById("player") as HTMLAudioElement,
      localAudioRenderer,
      App.createAudioElement(localAudioRenderer, true),
      localVideoRenderer,
      App.createVideoElement(localVideoRenderer),
      remoteAudioRenderer,
      App.createAudioElement(remoteAudioRenderer, false),
      remoteVideoRenderer,
      App.createVideoElement(remoteVideoRenderer));
  }

  protected async onAppInitWithLoggedInUser(user: User): Promise<void> {
    await super.onAppInitWithLoggedInUser(user);
    const keys: ParticipantKey[] = [];

    const botMembers: Member[] = [];
    const appPrefs = AppPrefs.getInstance();
    for (const persona of CHAT_PERSONAS) {
      const appearance = CHAT_PERSONA_APPEARANCE_KTM.findMetadata(persona.type);
      const user = new UserEntity(null, persona.displayName, null);
      let imageData: string = appPrefs.getImageData(persona.type);
      if (!imageData) {
        imageData = await Avatar.generateImageDataUrl(appearance.avatarConfig, appearance.color);
        appPrefs.setImageData(persona.type, imageData);
      }
      user.profilePhoto = imageData;
      const member = new Member(persona.type, null);
      member.user = user;
      botMembers.push(member);
    }
    Members.getInstance().setBotMembers(botMembers);

    const memberId = getMemberAuth().getMemberId();
    keys.push(new ParticipantKey(memberId, ParticipantType.MEMBER));

    keys.forEach(key => this.myParticipantKeys.set(key.type, key));
  }

  private readonly myParticipantKeys = new Map<ParticipantType, ParticipantKey>();

  getMyParticipantKey(type: ParticipantType): ParticipantKey {
    return this.myParticipantKeys.get(type);
  }

  protected getAuth(app: FirebaseApp): Auth {
    return Capacitor.isNativePlatform()
      ? initializeAuth(app, {
        persistence: indexedDBLocalPersistence
      })
      : super.getAuth(app);
  }

  getProvisionedAppId(): string {
    return "toktiv";
  }

  getProvisionedAppConfig(): ProvisionedAppConfig | null {
    return undefined;
  }

  onCreateAppConfig(): AppConfig {
    return {
      name: "TokTiv",
      icon: "/icon.png",
      logo: "/logo192.png",
      stamp: "/stamp.png",
      stampHeight: SZ_MD,
      safeAreaInsets: {
        top: Capacitor.getPlatform() === "ios" || Capacitor.getPlatform() === "android" ? 24 : 0,
        bottom: Capacitor.getPlatform() === "ios" || Capacitor.getPlatform() === "android" ? 16 : 0,
      },
      theme: {
        ...THEME,
        typography: {
          ...THEME.typography,
          fontSize: Capacitor.getPlatform() === "ios" || Capacitor.getPlatform() === "android" ? 13 : 15,
        },
      },
      firebaseConfig: {options: FIREBASE_CONFIG},
    };
  }

  static nestedPaths(): PathComponent[] {
    return Main.nestedPaths();
  }

  getAppPrefs<T extends BaseAppPrefs>(): T {
    //@ts-ignore
    return AppPrefs.getInstance() as T;
  }

  protected renderMain(): ReactElement {
    return <Main path={this.props.path}/>;
  }
}
